import { Box, Typography, CircularProgress } from "@mui/material";
import { colors } from "../../ui/colors";
import { IInvoiceMetrics } from "./actions/getInvoiceMetrics";

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount ?? 0);
};

const MetricCard = ({
  label,
  value,
  bgColor,
  borderColor,
  iconColor,
}: {
  label: string;
  value: string;
  bgColor: string;
  borderColor: string;
  iconColor: string;
}) => {
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: bgColor,
        p: .5,
        height: 150,
        borderRadius: 2,
        border: `1px solid transparent`,
        transition: "all 0.3s ease",
        "&:hover": {
          bgcolor: bgColor.replace("0.05", "0.08"),
          border: `1px solid ${borderColor}`,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 700,
          color: iconColor,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mb: 1.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: 700,
          color: colors.gray[700],
          mb: 1.5,
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      <Box sx={{ width: "100%", height: "20px", overflow: "visible", mt: 1 }}>
        <svg
          width="100%"
          height="20"
          preserveAspectRatio="none"
          viewBox="0 0 100 20"
          style={{
            color: iconColor + "66",
            overflow: "visible",
            display: "block"
          }}
        >
          <path
            d="M0,15 Q10,12 20,18 T40,10 T60,15 T80,5 T100,12"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </Box>
    </Box>
  );
};

export const MetricsContent = ({ metrics }: { metrics: IInvoiceMetrics }) => {
  const totalEarned = formatCurrency(metrics?.totalNetRevenue ?? 0);
  const pendingAmount = formatCurrency(metrics?.totalPendingRevenue ?? 0);
  const totalFees = formatCurrency(metrics?.totalFees ?? 0);

  const totalRevenue = (metrics?.totalNetRevenue ?? 0) + (metrics?.totalPendingRevenue ?? 0);
  const collectionRatio = totalRevenue > 0 ? ((metrics?.totalNetRevenue ?? 0) / totalRevenue) * 100 : 0;
  const pendingRatio = 100 - collectionRatio;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          gap: 0.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            label="Total Earned"
            value={totalEarned}
            bgColor={colors.success[50]}
            borderColor={colors.success[200]}
            iconColor={colors.success[600]}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            label="Pending"
            value={pendingAmount}
            bgColor={colors.warning[50]}
            borderColor={colors.warning[200]}
            iconColor={colors.warning[600]}
          />
        </Box>
      </Box>

      <Box sx={{ width: "100%", pt: 2, mt: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 1.25 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 400,
                color: colors.gray[500],
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Collection Ratio
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                color: colors.gray[600],
                mt: 0.5,
                mr: 1.5,
              }}
            >
              Paid vs. Outstanding
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: colors.success[600],
              }}
            >
              {collectionRatio.toFixed(1)}%
            </Typography>

            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 400,
                color: colors.gray[400],
                textTransform: "none",
                mt: 0.25,
              }}
            >
              Collected
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            height: 6,
            width: "100%",
            bgcolor: colors.gray[100],
            borderRadius: "999px",
            display: "flex",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              bgcolor: colors.success[600],
              width: `${collectionRatio}%`,
            }}
          />
          <Box
            sx={{
              height: "100%",
              bgcolor: colors.warning[400] + "66",
              width: `${pendingRatio}%`,
            }}
          />
        </Box>
      </Box>

      {metrics.revenueByPaymentMethod && (
        <Box
          sx={{
            width: "100%",
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${colors.gray[100]}`,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 400,
              color: colors.gray[500],
              textTransform: "none",
              letterSpacing: "0.01em",
              mb: 1,
            }}
          >
            By Payment Method
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
            {metrics.revenueByPaymentMethod?.stripe && (
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 400, color: colors.gray[500] }}>
                  Stripe
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 400, color: colors.gray[700], mt: 0.25 }}>
                  {formatCurrency(metrics.revenueByPaymentMethod.stripe?.netAmount ?? 0)}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 400, color: colors.gray[400], mt: 0.25 }}>
                  {metrics.revenueByPaymentMethod.stripe?.count ?? 0} payments
                </Typography>
              </Box>
            )}

            {metrics.revenueByPaymentMethod?.paypal && (
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 400, color: colors.gray[500] }}>
                  PayPal
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 400, color: colors.gray[700], mt: 0.25 }}>
                  {formatCurrency(metrics.revenueByPaymentMethod.paypal?.netAmount ?? 0)}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 400, color: colors.gray[400], mt: 0.25 }}>
                  {metrics.revenueByPaymentMethod.paypal?.count ?? 0} payments
                </Typography>
              </Box>
            )}

            {metrics.revenueByPaymentMethod?.bank_transfer && (
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 400, color: colors.gray[500] }}>
                  Bank Transfer
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 400, color: colors.gray[700], mt: 0.25 }}>
                  {formatCurrency(metrics.revenueByPaymentMethod.bank_transfer?.netAmount ?? 0)}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 400, color: colors.gray[400], mt: 0.25 }}>
                  {metrics.revenueByPaymentMethod.bank_transfer?.count ?? 0} payments
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
